import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Marketplace from "@/pages/Marketplace";
import ProductDetail from "@/pages/ProductDetail";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import LegalPage from "@/pages/LegalPage";
import LoansLanding from "@/pages/LoansLanding";
import SharedApplyLanding from "@/pages/SharedApplyLanding";
import InviteLanding from "@/pages/InviteLanding";
import MyReferrals from "@/pages/MyReferrals";
import { initTracking } from "@/lib/utm";
import { isAdminHost, isLoansHost, urlOnAdmin, urlOnPublic, shouldCrossHost } from "@/lib/hosts";

/** Redirect the browser cross-host (hard nav) when the requested route
 *  belongs on a different subdomain than the one we're currently on. */
function RedirectTo({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

/** If public visitor hits /login /admin /partner on the marketing site,
 *  bounce them to switch.offer.express/login (prod only).
 *  On preview/localhost, allow the route to render in-place. */
function PublicGuard({ children }) {
  const loc = useLocation();
  // ONLY /admin lives on the switch subdomain now. /login and /partner stay
  // on the public site so customers, partners, and staff never have to swap
  // domains just to sign in.
  const adminOnlyPaths = ["/admin"];
  if (shouldCrossHost() && adminOnlyPaths.some((p) => loc.pathname.startsWith(p))) {
    return <RedirectTo to={urlOnAdmin(loc.pathname + loc.search)} />;
  }
  return children;
}

/** On the admin host we ONLY expose /login + /admin + /partner. Any other
 *  path (including the landing "/") sends the visitor to the public site. */
function AdminOnlyShell() {
  const loc = useLocation();
  // Switch subdomain hosts ONLY the admin console. Everything else
  // (marketing, partner login, product pages) lives on the public site.
  const isAdminRoute = loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/login");
  if (!isAdminRoute) {
    if (loc.pathname === "/" || loc.pathname === "") {
      return <Navigate to="/login" replace />;
    }
    return <RedirectTo to={urlOnPublic(loc.pathname + loc.search)} />;
  }
  return (
    <>
      <Navbar />
      <main data-testid="app-main" className="pb-[76px] md:pb-0">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
        </Routes>
      </main>
      <Toaster position="top-right" richColors />
    </>
  );
}

function PublicShell() {
  return (
    <>
      <Navbar />
      <main data-testid="app-main" className="pb-[76px] md:pb-0">
        <PublicGuard>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            {/* Shared referral link — auto-opens mobile capture then redirects
                to the partner tracked URL. Works on both the main site and
                the loans subdomain (see LoansShell). */}
            <Route path="/apply/:productId" element={<SharedApplyLanding />} />
            <Route path="/i/:slug" element={<InviteLanding />} />
            <Route path="/m/:slug" element={<InviteLanding />} />
            <Route path="/m/:slug/:tag" element={<InviteLanding />} />
            <Route path="/my/referrals" element={<MyReferrals />} />
            {/* Admin routes rendered inline only on preview/localhost (no real
                switch.* subdomain to redirect to). On prod, PublicGuard bounces. */}
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
            <Route path="/partner" element={<ProtectedRoute roles={["partner", "admin"]}><Dashboard /></ProtectedRoute>} />
          </Routes>
        </PublicGuard>
      </main>
      <Footer />
      <MobileStickyCTA />
      <Toaster position="top-right" richColors />
    </>
  );
}

function LoansShell() {
  // Loans subdomain: minimal chrome, no Navbar/Footer — the LoansLanding
  // page ships its own header/footer sized for the loan-sales funnel.
  return (
    <>
      <main data-testid="app-main">
        <Routes>
          <Route path="/" element={<LoansLanding />} />
          <Route path="/apply/:productId" element={<SharedApplyLanding />} />
          <Route path="/i/:slug" element={<InviteLanding />} />
          {/* Anything else on this subdomain bounces to the main site */}
          <Route path="*" element={<RedirectTo to={urlOnPublic("/")} />} />
        </Routes>
      </main>
      <Toaster position="top-right" richColors />
    </>
  );
}

function AppShell() {
  const location = useLocation();
  useEffect(() => { initTracking(); }, []);
  // GA4 SPA pageview: fire on every route change so React Router
  // navigations are counted (the gtag snippet in index.html only fires
  // once at initial load).
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
  // /apply/:productId is ALWAYS rendered with zero shell chrome (no Navbar,
  // no Footer, no MobileStickyCTA) regardless of host — recipients of a
  // shared link see only the minimal black landing + mobile capture modal.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const isApply = path.startsWith("/apply/");
  const isInvite = path.startsWith("/i/");
  if (isApply || isInvite) {
    return (
      <>
        <Routes>
          <Route path="/apply/:productId" element={<SharedApplyLanding />} />
          <Route path="/i/:slug" element={<InviteLanding />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </>
    );
  }
  if (isAdminHost())  return <AdminOnlyShell />;
  if (isLoansHost())  return <LoansShell />;
  return <PublicShell />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
